import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Table,
  Alert,
  Accordion,
  Card,
  Image,
  Container,
  Row,
  Col,
  Dropdown
} from "react-bootstrap";
import "../css/CustomDomain.css";
import { jwtDecode } from "jwt-decode";
import customDomainIcon from "../assets/images/icons/custom-domain-icon.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { LiaAngleDownSolid, LiaAngleUpSolid } from "react-icons/lia";
import { FaEllipsisV } from "react-icons/fa";
import { RiDeleteBin5Line } from "react-icons/ri";
import { FaTrash } from "react-icons/fa";
import { FaQuestionCircle, FaStar } from "react-icons/fa";
import { domainService } from '../services/domainService';
import { accountService } from '../services/accountService';
import { userService } from '../services/userService';

const CustomDomain = () => {
  const [showModal, setShowModal] = useState(false);
  const [customDomain, setCustomDomain] = useState("");
  const [domainHistory, setDomainHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldError, setFieldError] = useState("");
  const [companyId, setCompanyId] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [domainToDelete, setDomainToDelete] = useState(null);
  const [showDnsSetupModal, setShowDnsSetupModal] = useState(false);

  const [companyDomains, setCompanyDomains] = useState({});

  const [dnsInfo, setDnsInfo] = useState({
    type: "A",
    name: "@",
    target: "168.220.91.95",
  });

  const [cnameInfo, setCnameInfo] = useState({
    type: "CNAME",
    name: "@",
    target: "dualstack.url-shortner-lb-982044127.us-west-1.elb.amazonaws.com",
  });

  const [isAdmin, setIsAdmin] = useState(false);

  const [selectedProtocol, setSelectedProtocol] = useState('http://');

  const [showHelpModal, setShowHelpModal] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(5);

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentDomains = Array.isArray(domainHistory) ? domainHistory.slice(indexOfFirstRecord, indexOfLastRecord) : [];
  const totalPages = Math.ceil((domainHistory?.length || 0) / recordsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const [showTable, setShowTable] = useState(true);

  useEffect(() => {
    const checkUserRole = () => {
      try {
        const token = localStorage.getItem("token");
        if (token) {
          const decodedToken = jwtDecode(token);
          setIsSuperAdmin(decodedToken.roles.includes("SUPERADMIN"));
          setIsAdmin(decodedToken.roles.includes("ADMIN"));
        }
      } catch (err) {
        console.error("Error decoding token:", err);
        setIsSuperAdmin(false);
        setIsAdmin(false);
      }
    };
    checkUserRole();
  }, []);

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      try {
        const token = localStorage.getItem("token");
        if (isSuperAdmin) {
          const response = await accountService.getAccountConfigs();
          setCompanies(response);
        } else {
          const decoded = jwtDecode(token);
          const username = decoded.sub;
          const userResponse = await userService.getUserByUsername(username);
          setCompanyId(userResponse.accountId);

          if (userResponse.accountId) {
            const domainsResponse = await domainService.getDomainsByAccount(userResponse.accountId);
            setDomainHistory(domainsResponse || []);
          }
        }
      } catch (err) {
        console.error('Error in fetchCompanyDetails:', err);
        setError("Failed to fetch company information: " + err.message);
        setDomainHistory([]); 
      }
    };

    fetchCompanyDetails();
  }, [isSuperAdmin]);

  const fetchDomains = async (companyId) => {
    try {
      const response = await domainService.getDomainsByAccount(companyId);
      return response || [];
    } catch (err) {
      console.error("Error fetching domains:", err);
      return [];
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const targetCompanyId = isSuperAdmin ? selectedCompany : companyId;

    if (!targetCompanyId) {
      setError("Please select a company.");
      return;
    }

    const fullDomain = selectedProtocol + customDomain;

    // Validate domain
    if (!customDomain.trim()) {
      setFieldError("Domain is required");
      return;
    }

    // Validate domain length (50 char limit)
    if (fullDomain.length > 50) {
      setFieldError("Domain must not exceed 50 characters");
      return;
    }

    setIsLoading(true);
    setError(null);
    setFieldError("");

    const computeCnameName = (d) => {
      if (!d || typeof d !== "string") return "@";
      let host = d.trim().toLowerCase();
      host = host.replace(/^(https?:\/\/)/, "");
      host = host.split("/")[0];
      host = host.replace(/\.$/, "");
      const parts = host.split(".").filter(Boolean);
      if (parts.length <= 2) return "@";
      const sub = parts.slice(0, 1).join(".");
      return sub ? `@${sub}` : "@";
    };

    try {
      const token = localStorage.getItem("token");
      await domainService.addDomainToAccount(targetCompanyId, fullDomain);

      const domainsResponse = await domainService.getDomainsByAccount(targetCompanyId);
      
      if (isSuperAdmin) {
        setCompanyDomains(prev => ({
          ...prev,
          [targetCompanyId]: domainsResponse || []
        }));
      } else {
        setDomainHistory(domainsResponse || []);
      }

      setCustomDomain("");
      setShowModal(false);
      setCnameInfo((prev) => ({ ...prev, name: computeCnameName(customDomain) }));
      setShowDnsSetupModal(true);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteDomain = async () => {
    try {
      const token = localStorage.getItem("token");
      await domainService.deleteDomain(domainToDelete.id);

      if (isSuperAdmin) {
        setCompanyDomains(prev => ({
          ...prev,
          [domainToDelete.companyId]: (prev[domainToDelete.companyId] || []).filter(
            domain => domain.id !== domainToDelete.id
          )
        }));
      } else {
        setDomainHistory(prev => prev.filter(domain => domain.id !== domainToDelete.id));
      }

      setShowDeleteModal(false);
      setDomainToDelete(null);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    }
  };

  const handleSetDefaultDomain = async (domain) => {
    try {
      await domainService.setDefaultDomain(companyId, domain.id);
      
      // Update the domain history to reflect the new default domain
      setDomainHistory(prev => prev.map(d => ({
        ...d,
        defaultDomain: d.id === domain.id
      })));
      
      // Show success message
      setError(null);
      // You might want to add a success toast here
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    }
  };

  const handleAccordionSelect = async (companyId) => {
    if (!companyDomains[companyId]) {
      try {
        const token = localStorage.getItem("token");
        const response = await domainService.getDomainsByAccount(companyId);
        setCompanyDomains((prev) => ({
          ...prev,
          [companyId]: response || [],
        }));
      } catch (err) {
        if (err.response?.status !== 404) {
          setError(err.response?.data?.message || err.message);
        } else {
          setCompanyDomains((prev) => ({
            ...prev,
            [companyId]: [],
          }));
        }
      }
    }
  };

  const toggleTable = () => {
    setShowTable(!showTable);
  };

  return (
    <Container
      fluid
      className="ps-0 pb-4 pe-0 overflow-hidden user-management-container bg-white"
    >
      <Card className="card-nostyle">
        <Card.Body className="ps-0 pe-0">
          <div className="custom-domain-containers">
            <div
              className="d-flex justify-content-between ps-4 pe-4 align-items-center"
              style={{ borderBottom: "1px solid #EAECF0" }}
            >
              <h4 className="mb-0 bg-white br-radius-12 rounded-bottom-0 pb-3 fs-20 poppins fw-600">
                <Image src={customDomainIcon} className="me-1" width="20" />{" "}
                Custom Domain Management
              </h4>
            </div>
          </div>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {isSuperAdmin ? (
        <Card className="mb-4">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h4 className="mb-0">Domain Management Dashboard</h4>
              <Button
                variant="success"
                className="rounded-pill"
                onClick={() => setShowModal(true)}
              >
                Add Domain
              </Button>
            </div>
            <Accordion onSelect={handleAccordionSelect}>
              {companies.map((company) => (
                <Accordion.Item key={company.id} eventKey={company.id}>
                  <Accordion.Header>
                    <div className="d-flex align-items-center">
                      {company.accountName}
                      {company.isActive ? (
                        <span className="badge bg-success ms-2">Active</span>
                      ) : (
                        <span className="badge bg-secondary ms-2">
                          Inactive
                        </span>
                      )}
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Table responsive hover>
                      <thead>
                        <tr>
                          <th>Domain</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(companyDomains[company.id] || []).length === 0 ? (
                          <tr>
                            <td colSpan="2" className="text-center">
                              <div className="py-3">No Domains Found</div>
                            </td>
                          </tr>
                        ) : (
                          (companyDomains[company.id] || []).map((domain) => (
                            <tr key={domain.id}>
                              <td>{domain.domain}</td>
                              <td>
                                <div className="d-flex gap-2">
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => {
                                      setDomainToDelete({
                                        id: domain.id,
                                        domain: domain.domain,
                                        companyId: company.id,
                                      });
                                      setShowDeleteModal(true);
                                    }}
                                  >
                                    Delete
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          </Card.Body>
        </Card>
      ) : isAdmin ? (
        <>
          <Row className="ps-4 pe-4">
            <Col lg={12}>
              <Card className="br-radius-12">
                <Card.Header className="bg-white br-radius-12 rounded-bottom-0 pt-3 pb-3 fs-14 br-color-EAECEE fw-600">
                  Add Custom Domain
                </Card.Header>
                <Card.Body>
                  <div className="domain-info">
                    <p className="text-616161 mb-0 fs-11 p-0 fw-400 ls-spacing-0">
                      <span className="fw-600 text-dark">Note : </span>Custom
                      domains are a great way to brand your company. Adding a
                      Custom Domain to your account will allow you to create
                      short links with your domain.
                    </p>
                  </div>
                  <Form onSubmit={handleSubmit} className="domain-forms" noValidate>
                    <Form.Group as={Row} className="mb-3">
                      <Form.Label column sm="2" className="fs-12 form-label">
                        Custom Domain
                      </Form.Label>
                      <Col sm="8">
                        <div className="d-flex align-items-center">
                          <Form.Select 
                            className="w-auto me-2 fs-12"
                            value={selectedProtocol}
                            onChange={(e) => setSelectedProtocol(e.target.value)}
                            style={{ minWidth: '100px' }}
                          >
                            <option value="http://">http://</option>
                            <option value="https://">https://</option>
                          </Form.Select>
                          <Form.Control
                            type="text"
                            className="flex-grow-1 fs-12"
                            value={customDomain}
                            onChange={(e) => {
                              const value = e.target.value.replace(/^(https?:\/\/)/, '');
                              setCustomDomain(value);
                              if (fieldError) setFieldError("");
                            }}
                            placeholder="Enter your custom domain"
                            maxLength={43}
                            isInvalid={!!fieldError}
                            required
                            disabled={isLoading}
                          />
                        </div>
                        {fieldError ? (
                          <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>
                            {fieldError}
                          </div>
                        ) : (
                          <Form.Text className="text-muted d-block mt-1 fs-11">
                            Max 50 characters total (including protocol)
                          </Form.Text>
                        )}
                      </Col>
                    </Form.Group>

                    <Button
                      variant="primary"
                      className="float-end"
                      type="submit"
                      disabled={isLoading}
                    >
                      {/* {isLoading ? "Adding..." : "+ Add"} */}
                      {isLoading ? (
                        "Adding..."
                      ) : (
                        <>
                          <FontAwesomeIcon
                            icon={faPlus}
                            className="fs-16 me-1"
                          />
                          Add Domain
                        </>
                      )}
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <div className="domain-history mt-4 pe-4 ps-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <Button
                style={{ fontFamily: "General Sans Medium" }}
                variant="outline-dark br-color-0C1116 fs-12 fw-500 text-242D35 pe-3 px-3 pt-1 pb-1 GeneralSansMedium d-flex align-items-center"
                onClick={toggleTable}
              >
                History{" "}
                {showTable ? (
                  <span className="arrow-icon">
                    <LiaAngleUpSolid />
                  </span>
                ) : (
                  <span className="arrow-icon">
                    <LiaAngleDownSolid />
                  </span>
                )}
              </Button>

              <Button
                variant="success"
                className="fs-12 d-flex align-items-center gap-2 br-radius-8"
                onClick={() => setShowHelpModal(true)}
              >
                <FaQuestionCircle className="" />
                Domain Setup Guide
              </Button>
            </div>

            {showTable && (
              <>
                <Table responsive hover className="unset-table mb-0 table-hover-border table-overflow-unset overflow-unset">
                  <thead>
                    <tr>
                      <th>Domain</th>
                      <th>Status</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(currentDomains) && currentDomains.length === 0 ? (
                      <tr>
                        <td colSpan="3" className="text-center">
                          <div className="py-3">No Domains Found</div>
                        </td>
                      </tr>
                    ) : (
                      Array.isArray(currentDomains) && currentDomains.map((domain) => (
                        <tr key={domain.id}>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <p
                                className="text-success text-td-success mb-0 text-prewrap"
                                style={{ color: "#00C76F !important" }}
                              >
                                {domain.domain}
                              </p>
                              {domain.defaultDomain && (
                                <span
                                  className="badge bg-success"
                                  style={{ fontSize: "8px" }}
                                >
                                  Default
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            <span 
                              className={`badge ${domain.assigned ? 'bg-success' : 'bg-danger'}`}
                            >
                              {domain.assigned ? 'Verified' : 'Not Verified'}
                            </span>
                          </td>
                          <td className="text-end">
                            <Dropdown className="cust-drop">
                              <Dropdown.Toggle
                                variant="link"
                                id="dropdown-basic"
                              >
                                <FaEllipsisV className="text-A8B0B9 fs-12" />
                              </Dropdown.Toggle>
                              <Dropdown.Menu>
                                {!domain.defaultDomain && (
                                   <Dropdown.Item
                                     href="#"
                                     onClick={() => handleSetDefaultDomain(domain)}
                                     className="fs-12 pt-2 pb-2"
                                   >
                                     <FaStar className="align-middle fs-12 me-1" />
                                     Set as Default
                                   </Dropdown.Item>
                                 )}
                                <Dropdown.Item
                                  className="fs-12 pt-2 pb-2"
                                  href="#"
                                  onClick={() => {
                                    setDomainToDelete({
                                      id: domain.id,
                                      domain: domain.domain,
                                      companyId: companyId,
                                    });
                                    setShowDeleteModal(true);
                                  }}
                                >
                                  <RiDeleteBin5Line className="align-middle fs-14 me-1" />
                                  Delete
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
                
                {Array.isArray(domainHistory) && domainHistory.length > recordsPerPage && (
                  <div className="d-flex justify-content-center mt-3">
                    <nav>
                      <ul className="pagination mb-2">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => paginate(1)}
                            disabled={currentPage === 1}
                          >
                            &laquo;
                          </button>
                        </li>
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => paginate(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            &lt;
                          </button>
                        </li>
                        {(() => {
                          let startPage = Math.max(currentPage - 1, 1);
                          let endPage = Math.min(startPage + 2, totalPages);
                          
                          if (endPage === totalPages) {
                            startPage = Math.max(endPage - 2, 1);
                          }

                          return [...Array(endPage - startPage + 1)].map((_, idx) => (
                            <li
                              key={startPage + idx}
                              className={`page-item ${currentPage === startPage + idx ? 'active' : ''}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => paginate(startPage + idx)}
                              >
                                {startPage + idx}
                              </button>
                            </li>
                          ));
                        })()}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => paginate(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            &gt;
                          </button>
                        </li>
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => paginate(totalPages)}
                            disabled={currentPage === totalPages}
                          >
                            &raquo;
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="domain-info">
            <p>
              Custom domains are a great way to brand your company. Adding a
              Custom Domain to your account will allow you to create short links
              with your domain. For more information, check out{" "}
              <a href="#" className="text-danger">
                this guide
              </a>{" "}
              or contact{" "}
              <a href="#" className="text-danger">
                support
              </a>
            </p>
          </div>

          <Form onSubmit={handleSubmit} className="domain-form" noValidate>
            <Form.Group className="mb-3">
              <Form.Label>Custom Domain</Form.Label>
              <Form.Control
                type="text"
                value={customDomain}
                onChange={(e) => setCustomDomain(e.target.value)}
                placeholder="Enter your custom domain"
                maxLength={50}
                required
                disabled={isLoading}
              />
              <Form.Text className="text-muted">
                Max 50 characters
              </Form.Text>
            </Form.Group>

            <Button variant="primary" type="submit" disabled={isLoading}>
              {isLoading ? "Adding..." : "+ Add"}
            </Button>
          </Form>

          <div className="domain-history mt-4">
            <h3>Domain History</h3>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Domain</th>
                </tr>
              </thead>
              <tbody>
                {currentDomains.length === 0 ? (
                  <tr>
                    <td colSpan="1" className="text-center">
                      <div className="py-3">No Domains Found</div>
                    </td>
                  </tr>
                ) : (
                  currentDomains.map((domain) => (
                    <tr key={domain.id}>
                      <td>{domain.domain}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
            
            {Array.isArray(domainHistory) && domainHistory.length > recordsPerPage && (
              <div className="d-flex justify-content-center mt-3">
                <nav>
                  <ul className="pagination mb-2">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => paginate(1)}
                        disabled={currentPage === 1}
                      >
                        &laquo;
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        &lt;
                      </button>
                    </li>
                    {(() => {
                      let startPage = Math.max(currentPage - 1, 1);
                      let endPage = Math.min(startPage + 2, totalPages);
                      
                      // Adjust startPage if we're at the end
                      if (endPage === totalPages) {
                        startPage = Math.max(endPage - 2, 1);
                      }

                      return [...Array(endPage - startPage + 1)].map((_, idx) => (
                        <li
                          key={startPage + idx}
                          className={`page-item ${currentPage === startPage + idx ? 'active' : ''}`}
                        >
                          <button
                            className="page-link"
                            onClick={() => paginate(startPage + idx)}
                          >
                            {startPage + idx}
                          </button>
                        </li>
                      ));
                    })()}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        &gt;
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => paginate(totalPages)}
                        disabled={currentPage === totalPages}
                      >
                        &raquo;
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add New Domain</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit} noValidate>
            <Form.Group className="mb-3">
              <Form.Label>Select Company</Form.Label>
              <Form.Select
                value={selectedCompany || ""}
                onChange={(e) => setSelectedCompany(e.target.value)}
                required
              >
                <option value="">Choose a company...</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.accountName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Domain</Form.Label>
              <Form.Control
                type="text"
                value={customDomain}
                onChange={(e) => setCustomDomain(e.target.value)}
                placeholder="Enter custom domain"
                maxLength={50}
                required
              />
              <Form.Text className="text-muted">
                Max 50 characters
              </Form.Text>
            </Form.Group>
            <div className="d-flex gap-2">
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button
                variant="success"
                type="submit"
                disabled={isLoading || !selectedCompany}
              >
                {isLoading ? "Adding..." : "Add Domain"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >

        <Modal.Header closeButton className="border-0 pb-2 bg-white">
        </Modal.Header>
        <Modal.Body className="text-center pt-0">
        <h5 className="text-101828 mb-1 fw-600 ls-minus-0 fs-14">Are you sure you want to delete this domain?</h5>
        <p className='fw-400 fs-12 mb-1'>
          You will not be able to recover this domain! 
          <span className="text-success text-td-success mb-0 d-block">"{domainToDelete?.domain}"?</span>
          </p>
          
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button variant="light" onClick={() => setShowDeleteModal(false)}>
          Don't delete
          </Button>
          <Button variant="danger" onClick={handleDeleteDomain}>
          YES, delete
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDnsSetupModal}
        onHide={() => setShowDnsSetupModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton className="border-0 bg-white">
          <Modal.Title className="w-100 text-center text-101828 mb-1 fw-600 ls-minus-0 fs-14">
            Finish Domain Setup
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="">
          <div className="domain-help-content">
            <ol className="ps-3">
              <li className="mb-3">
                <strong>Domain Connection Instructions</strong>
                <p className="text-muted mb-2">
                  Thanks for adding your custom domain. To finish connecting your
                  domain, please update the A record or CNAME record for a subdomain
                  for your domain by visiting your DNS provider or domain registrar.
                  Make sure this is your only A record for this domain. Also, delete
                  any additional AAAA records.
                </p>
              </li>

              <li className="mb-3">
                <strong>Option #2 CNAME Record</strong>
                <div className="bg-light p-3 rounded mb-2">
                  <Table responsive bordered className="mb-0">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Name</th>
                        <th>Target</th>
                      </tr>
                    </thead>
                      <tbody>
                        <tr>
                          <td>{cnameInfo.type}</td>
                          <td>{cnameInfo.name}</td>
                          <td>{cnameInfo.target}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
              </li>

              

              

              <li className="mb-4">
                <strong>Important Notes:</strong>
                <ul className="text-muted mt-2">
                  <li>Make sure this is your only A record for this domain</li>
                  <li>Delete any additional AAAA records</li>
                  <li>If using Cloudflare, ensure the "Orange Cloud" or Proxy Status is not enabled</li>
                </ul>
              </li>

              <li className="mb-4">
                <strong>DNS Provider Setup Guides</strong>
                <div className="bg-light p-3 rounded mb-2">
                <Row>
                  <Col md={6}>
                    <ul className="list-unstyled">
                      <li className="mb-2">
                        <a href="https://support.google.com/domains/answer/3290350" className="text-success" target="_blank" rel="noopener noreferrer">
                          Google Documentation
                        </a>
                      </li>
                      <li className="mb-2">
                        <a href="https://www.godaddy.com/help/add-an-a-record-19238" className="text-success" target="_blank" rel="noopener noreferrer">
                          GoDaddy Documentation
                        </a>
                      </li>
                    </ul>
                  </Col>
                  <Col md={6}>
                    <ul className="list-unstyled">
                      <li className="mb-2">
                        <a href="https://www.bluehost.com/help/article/dns-management-add-edit-or-delete-dns-entries" className="text-success" target="_blank" rel="noopener noreferrer">
                          BlueHost Documentation
                        </a>
                      </li>
                                       <li className="mb-2">
                        <a href="https://support.hostgator.com/articles/how-to-manage-dns-records-with-hostgatorenom" className="text-success" target="_blank" rel="noopener noreferrer">
                          HostGator Documentation
                        </a>
                      </li>
                    </ul>
                  </Col>
                </Row>
                </div>
              </li>

              <li className="mb-4">
                <strong>DNS Propagation</strong>
                <p className="text-muted">
                  DNS changes can take up to 24 hours to propagate worldwide,
                  although most often this happens in a matter of hours. We will
                  automatically verify your DNS change and send you an email. Please
                  check back in a few minutes.
                </p>
              </li>
            </ol>

            <div className="alert alert-info mt-4">
               <strong>Need Help?</strong>
               <p className="mb-0">If you need assistance with domain setup, please contact our support team at <a href="mailto:support@comsensetechnologies.com" className="text-success">support@comsensetechnologies.com</a></p>
             </div>
           </div>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 p-3">
          <div className="d-flex gap-3 w-100">
            <Button
              variant="success"
              className="flex-grow-1"
              onClick={() => setShowDnsSetupModal(false)}
            >
              Got It
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showHelpModal}
        onHide={() => setShowHelpModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton className="border-0 bg-white">
          <Modal.Title className="w-100 text-center text-101828 fw-600 ls-minus-0 fs-14">
            Domain Setup Guide
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="">
          <div className="domain-help-content">
              <ol className="ps-3">
                <li className="mb-2">
                  <strong>Add DNS Records</strong>
                  <p className="text-muted mb-2">Add the following DNS records to your domain's DNS settings:</p>
                  <div className="bg-light p-3 rounded mb-2 mt-3">
                    <Table responsive bordered className="mb-0">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Name</th>
                          <th>Target</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>CNAME</td>
                          <td>{cnameInfo.name}</td>
                          <td>dualstack.url-shortner-lb-982044127.us-west-1.elb.amazonaws.com</td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </li>

                <li className="mb-4">
                  <strong>Important Notes:</strong>
                  <ul className="text-muted mt-2">
                    <li>Make sure this is your only A record for this domain</li>
                    <li>Delete any additional AAAA records</li>
                    <li>If using Cloudflare, ensure the "Orange Cloud" or Proxy Status is not enabled</li>
                  </ul>
                </li>

                <li className="mb-4">
                  <strong>DNS Propagation</strong>
                  <p className="text-muted">DNS changes can take up to 24-48 hours to propagate globally. Please be patient during this time.</p>
                </li>
              </ol>

              {/* <div className="help-documentation mt-4">
                <h6 className="mb-3">DNS Provider Setup Guides</h6>
                <Row>
                  <Col md={6}>
                    <ul className="list-unstyled">
                      <li className="mb-2"><a href="#" className="text-success">Google Domains Setup Guide</a></li>
                      <li className="mb-2"><a href="#" className="text-success">GoDaddy Setup Guide</a></li>
                      <li className="mb-2"><a href="#" className="text-success">HostGator Setup Guide</a></li>
                    </ul>
                  </Col>
                  <Col md={6}>
                    <ul className="list-unstyled">
                      <li className="mb-2"><a href="#" className="text-success">Register.com Setup Guide</a></li>
                      <li className="mb-2"><a href="#" className="text-success">BlueHost Setup Guide</a></li>
                      <li className="mb-2"><a href="#" className="text-success">Cloudflare Setup Guide</a></li>
                    </ul>
                  </Col>
                </Row>
              </div> */}

              <div className="alert alert-info mt-4">
                <strong>Need Help?</strong>
                <p className="mb-0">If you need assistance with domain activation, please contact our support team at support@comsensetechnologies.com</p>
              </div>
            </div>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 p-3">
          <div className="d-flex gap-3 w-100">
            <Button
              variant="success"
              className="flex-grow-1"
              onClick={() => setShowHelpModal(false)}
            >
              Got It
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CustomDomain;
